/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// ResetPassword.tsx
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff } from "lucide-react";

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Use debounced token to prevent rapid API calls
  const debouncedToken = useDebounce(token || "", 500); // 500ms delay

  // Add ref to track if verification is in progress
  const verificationInProgress = useRef(false);
  const hasVerified = useRef(false);

  const { verifyResetToken, resetPassword, loading, error, clearError } =
    useAuth();

  // Handle password changes with real-time validation
  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    const errors = validatePassword(newPassword);
    setPasswordErrors(errors);
  };

  // Verify token on component mount - FIXED VERSION with debounce
  useEffect(() => {
    const verifyToken = async () => {
      // Prevent multiple simultaneous verifications
      if (verificationInProgress.current || hasVerified.current) {
        return;
      }

      if (!debouncedToken) {
        setTokenValid(false);
        setMessage("Invalid reset link. Please request a new one.");
        hasVerified.current = true;
        return;
      }

      verificationInProgress.current = true;
      setTokenValid(null); // Reset to loading state

      try {
        console.log("Verifying reset token...", debouncedToken);
        const isValid = await verifyResetToken(debouncedToken);
        console.log("Token validation result:", isValid);
        setTokenValid(isValid);

        if (!isValid) {
          setMessage(
            "Invalid or expired reset link. Please request a new one.",
          );
        }
      } catch (error: any) {
        console.error("Token verification error:", error);
        setTokenValid(false);
        // Don't show rate limit errors as "invalid link"
        if (error.message?.includes("Too many authentication attempts")) {
          setMessage("Please wait a moment and refresh the page to try again.");
        } else {
          setMessage(
            error.message || "Failed to verify reset link. Please try again.",
          );
        }
      } finally {
        verificationInProgress.current = false;
        hasVerified.current = true;
      }
    };

    // Only verify if we have a debounced token
    if (debouncedToken) {
      verifyToken();
    } else if (token === null) {
      // If no token at all, show invalid message immediately
      setTokenValid(false);
      setMessage("Invalid reset link. Please request a new one.");
      hasVerified.current = true;
    }

    // Cleanup function
    return () => {
      verificationInProgress.current = false;
    };
  }, [debouncedToken, verifyResetToken]); // Use debouncedToken instead of token

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!token) {
      setMessage("Invalid reset link");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match!");
      return;
    }

    const passwordValidationErrors = validatePassword(password);
    if (passwordValidationErrors.length > 0) {
      setMessage("Please fix password requirements below");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      await resetPassword(token, password);
      setMessage("Password reset successfully! Redirecting to login...");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error: any) {
      setMessage(
        error.message || "Failed to reset password. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // If rate limited, show retry option
  if (message.includes("Please wait a moment")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50 px-6 py-12">
        <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-8 border border-sky-100 text-center">
          <h1 className="text-2xl font-bold text-yellow-600 mb-4">
            Please Wait
          </h1>
          <p className="text-gray-600 mb-6">{message}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-md hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
            >
              Retry Now
            </button>
            <button
              onClick={() => navigate("/forgot-password")}
              className="w-full py-3 bg-gray-500 text-white font-medium rounded-md hover:bg-gray-600 transition-all duration-300"
            >
              Request New Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === null && debouncedToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50 px-6 py-12">
        <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-8 border border-sky-100 text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Invalid Reset Link
          </h1>
          <p className="text-gray-600 mb-6">{message}</p>
          <button
            onClick={() => navigate("/forgot-password")}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-md hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50 px-6 py-12">
      <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-8 border border-sky-100">
        {/* Header */}
        <h1 className="text-2xl font-bold text-center bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
          Reset Password
        </h1>
        <p className="text-center text-gray-500 mt-2">
          Enter your new password below.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* New Password */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                required
                className="w-full px-4 py-2 border border-sky-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition pr-10"
                placeholder="Min 8 chars, upper, lower, number, special"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password Requirements */}
            {password && (
              <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Password must contain:
                </p>
                <ul className="text-xs space-y-1">
                  <li
                    className={`flex items-center ${password.length >= 8 ? "text-green-600" : "text-gray-500"}`}
                  >
                    <span className="mr-1">
                      {password.length >= 8 ? "✓" : "•"}
                    </span>
                    At least 8 characters
                  </li>
                  <li
                    className={`flex items-center ${/(?=.*[a-z])/.test(password) ? "text-green-600" : "text-gray-500"}`}
                  >
                    <span className="mr-1">
                      {/(?=.*[a-z])/.test(password) ? "✓" : "•"}
                    </span>
                    One lowercase letter
                  </li>
                  <li
                    className={`flex items-center ${/(?=.*[A-Z])/.test(password) ? "text-green-600" : "text-gray-500"}`}
                  >
                    <span className="mr-1">
                      {/(?=.*[A-Z])/.test(password) ? "✓" : "•"}
                    </span>
                    One uppercase letter
                  </li>
                  <li
                    className={`flex items-center ${/(?=.*\d)/.test(password) ? "text-green-600" : "text-gray-500"}`}
                  >
                    <span className="mr-1">
                      {/(?=.*\d)/.test(password) ? "✓" : "•"}
                    </span>
                    One number
                  </li>
                  <li
                    className={`flex items-center ${/(?=.*[@$!%*?&])/.test(password) ? "text-green-600" : "text-gray-500"}`}
                  >
                    <span className="mr-1">
                      {/(?=.*[@$!%*?&])/.test(password) ? "✓" : "•"}
                    </span>
                    One special character (@$!%*?&)
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-sky-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition pr-10"
                placeholder="Confirm your password"
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className="mt-2">
                <p
                  className={`text-sm ${password === confirmPassword ? "text-green-600" : "text-red-600"}`}
                >
                  {password === confirmPassword
                    ? "✓ Passwords match"
                    : "✗ Passwords do not match"}
                </p>
              </div>
            )}
          </div>

          {/* Message */}
          {message && (
            <div
              className={`p-3 rounded-md ${
                message.includes("successfully")
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {message}
            </div>
          )}

          {/* Error from AuthContext */}
          {error && (
            <div className="p-3 rounded-md bg-red-50 text-red-700">
              {error.toString()}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || loading || passwordErrors.length > 0}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-md hover:from-sky-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading || loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

const validatePassword = (password: string) => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/(?=.*[@$!%*?&])/.test(password)) {
    errors.push(
      "Password must contain at least one special character (@$!%*?&)",
    );
  }

  return errors;
};
